import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import { translations, type Language } from '@/lib/i18n';
import { supabase } from '@/integrations/supabase/client';

/**
 * i18n loading strategy (structural):
 *
 * 1. INLINE bundle (src/lib/i18n.ts) is loaded synchronously as a basic seed for all languages
 *    so the app never crashes on a missing key during first paint.
 *
 * 2. ENGLISH (the source language) is loaded from static JSON files in /public/locales/en/<ns>.json
 *    via i18next-http-backend.
 *
 * 3. ALL OTHER LANGUAGES are loaded EXCLUSIVELY from the `ui_translations` table in the database.
 *    Static JSON files for these languages are intentionally NOT used because:
 *      - They don't always exist on disk (the dev server returns index.html with status 200,
 *        which corrupts the namespace bundle when http-backend tries to JSON.parse the HTML).
 *      - The admin translation manager is the single source of truth for non-source languages.
 *
 * 4. Language activation (`changeLanguage`) is only called AFTER all required namespaces for the
 *    target language are fully loaded into the i18n store. This eliminates "English flash" and
 *    silent fallback to the source language.
 */

const NAMESPACES = ['common', 'landing', 'calculator', 'dashboard', 'discover', 'blog', 'city', 'studio', 'admin'];
const SOURCE_LANGUAGE = 'en';

const resources: Record<string, { translation: Record<string, string> }> = {};
for (const [lang, keys] of Object.entries(translations)) {
  resources[lang] = { translation: keys };
}

const languageLoadCache = new Map<string, Promise<void>>();

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    partialBundledLanguages: true,
    fallbackLng: SOURCE_LANGUAGE,
    supportedLngs: Object.keys(translations) as Language[],
    ns: ['translation', ...NAMESPACES],
    defaultNS: 'translation',
    fallbackNS: ['translation', 'common'],
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'inkvaluator_lang',
      caches: ['localStorage'],
    },
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
      // Only EN actually has static files; the backend should silently fail for others.
      requestOptions: { cache: 'default' },
      // Custom parse: validates the response is real JSON; rejects HTML-shaped bodies.
      parse: (data: string) => {
        const trimmed = (data ?? '').trim();
        if (!trimmed || trimmed.startsWith('<')) {
          // Vite dev / SPA hosts often return index.html with 200 for missing assets.
          throw new Error('not-json');
        }
        return JSON.parse(trimmed);
      },
    },
    react: {
      useSuspense: false,
      bindI18nStore: 'added removed',
    },
    returnEmptyString: false,
    returnNull: false,
  });

/** Fetch all DB-stored translations for a language and merge them into the i18n store. */
async function loadDbOverridesForLanguage(lng: string): Promise<void> {
  if (!lng || lng === 'cimode') return;

  try {
    const { data, error } = await supabase
      .from('ui_translations' as any)
      .select('namespace, content, status')
      .eq('language_code', lng)
      .in('status', ['complete', 'manual', 'partial', 'stale']);

    if (error || !data) return;

    for (const row of data as any[]) {
      const content = row.content as Record<string, string> | null;
      if (!content || typeof content !== 'object') continue;

      const namespace = row.namespace || 'translation';
      // deep=true, overwrite=true so DB wins over any inline/static seed.
      i18n.addResourceBundle(lng, namespace, content, true, true);
      (i18n.store as any).emit?.('added', lng, namespace);
    }
  } catch {
    /* DB unavailable — silently fall back to bundled/static translations */
  }
}

/** Load static JSON files (only meaningful for EN). Errors are swallowed. */
async function loadStaticNamespaces(lng: string): Promise<void> {
  if (lng !== SOURCE_LANGUAGE) return;
  await Promise.all(
    NAMESPACES.map((ns) => i18n.reloadResources(lng, ns).catch(() => undefined)),
  );
}

/**
 * Guarantees that all namespaces for `lng` are fully loaded into the i18n store.
 * Safe to call concurrently — repeated calls share the same in-flight promise.
 */
export async function ensureI18nLanguageReady(lng: string): Promise<void> {
  const normalizedLng = (lng || SOURCE_LANGUAGE).split('-')[0];

  if (!languageLoadCache.has(normalizedLng)) {
    const loadPromise = (async () => {
      try {
        // Always make sure the EN baseline is present so untranslated keys can fall back cleanly.
        if (normalizedLng !== SOURCE_LANGUAGE) {
          await loadStaticNamespaces(SOURCE_LANGUAGE);
        }
        await loadStaticNamespaces(normalizedLng);
        await loadDbOverridesForLanguage(normalizedLng);
      } finally {
        // Drop the cache entry only on failure so future retries get a fresh attempt.
      }
    })();

    languageLoadCache.set(normalizedLng, loadPromise);

    loadPromise.catch(() => {
      languageLoadCache.delete(normalizedLng);
    });
  }

  await languageLoadCache.get(normalizedLng);
}

// Bootstrap: load the initial detected language synchronously where possible.
const initialLng = (i18n.language || SOURCE_LANGUAGE).split('-')[0];
ensureI18nLanguageReady(initialLng);

// Whenever i18next switches language (including programmatic changes), make sure resources are
// fully loaded. This is a safety net — `setLanguage` in I18nContext awaits this BEFORE switching.
i18n.on('languageChanged', (lng) => {
  ensureI18nLanguageReady(lng).catch(() => undefined);
});

export default i18n;
